<template>
  <div class="confirm-modal">
    <div
        v-if="show"
        ref="dialog"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="messageId"
        @keydown.esc.stop="reject"
        @keydown.tab="trapFocus"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <p :id="messageId" class="confirm-modal__message">
              {{ message }}
            </p>
            <div class="confirm-modal__actions">
              <button ref="confirmButton" @click="approve" type="button" class="btn btn-success">Подтвердить</button>
              <button @click="reject" type="button" class="btn btn-outline-secondary">Отмена</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="show" class="modal-backdrop fade show" @click="reject"></div>
  </div>
</template>

<script>
let instanceCount = 0;

export default {
  name: "ConfirmModal",
  data() {
    return {
      show: false,
      message: null,
      resolve: null,
      messageId: `confirm-modal-message-${++instanceCount}`,
      // Element that had focus before the dialog opened, so we can hand focus
      // back where the user left it.
      previouslyFocused: null
    };
  },
  mounted() {
    this.confirmToken = this.$confirm.subscribe(({ message, resolve }) => {
      this.message = message;
      this.resolve = resolve;
      this.previouslyFocused = document.activeElement;
      this.show = true;
      this.$nextTick(() => this.$refs.confirmButton?.focus());
    });
  },
  unmounted() {
    this.$confirm.unsubscribe(this.confirmToken);
  },
  methods: {
    close() {
      this.show = false;
      // Restoring focus matters most for keyboard users: without it, focus
      // falls back to <body> and the next Tab restarts from the top of the page.
      const target = this.previouslyFocused;
      this.previouslyFocused = null;
      if (target && typeof target.focus === "function") {
        this.$nextTick(() => target.focus());
      }
    },
    approve() {
      const resolve = this.resolve;
      this.close();
      resolve && resolve();
    },
    reject() {
      this.close();
    },
    /** Keeps Tab cycling inside the dialog instead of escaping to the page behind. */
    trapFocus(event) {
      const focusable = this.$refs.dialog?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
};
</script>

<style lang="scss">
.confirm-modal {
  .modal {
    z-index: var(--z-modal);
  }

  .modal-dialog {
    width: 80%;
    // Vertically centre instead of pinning to `top: 40%`, which pushed the
    // dialog off-screen once the message wrapped to several lines.
    margin: 0 auto;
    display: flex;
    align-items: center;
    min-height: 100%;

    @media (min-width: 576px) { // Bootstrap SM
      width: auto;
    }

    .modal-content {
      width: 100%;
      border: none;
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);

      .modal-body {
        text-align: center;
        padding: 1.75rem 1.5rem 1.5rem;
      }
    }
  }

  &__message {
    margin: 0 0 1.5rem;
    font-size: 1.0625rem;
    line-height: 1.5;
    text-wrap: pretty;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    justify-content: center;

    .btn {
      // Was a fixed 8rem, which overflowed the dialog on narrow phones.
      flex: 1 1 auto;
      min-width: 7rem;
      max-width: 11rem;
    }
  }
}
</style>
